// Appearance: 'system' (follow the OS), 'light' or 'dark'.
// public/theme-init.js applies the saved choice before the first paint; this module
// keeps it in sync afterwards and lets components read and change it.
import { useEffect, useState, useCallback } from 'react';

const STORAGE_KEY = 'pp_theme';
const THEMES = ['system', 'light', 'dark'];
const THEME_COLORS = { light: '#f5f5f7', dark: '#161618' };
const listeners = new Set();

function systemPrefersDark() {
  return typeof window !== 'undefined' && !!window.matchMedia &&
    window.matchMedia('(prefers-color-scheme: dark)').matches;
}

export function getTheme() {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    return THEMES.includes(saved) ? saved : 'system';
  } catch {
    return 'system';
  }
}

export function resolveTheme(theme) {
  if (theme === 'light' || theme === 'dark') return theme;
  return systemPrefersDark() ? 'dark' : 'light';
}

export function applyTheme(theme) {
  if (typeof document === 'undefined') return;
  const resolved = resolveTheme(theme);
  const root = document.documentElement;
  root.classList.toggle('dark', resolved === 'dark');
  root.style.colorScheme = resolved;
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute('content', THEME_COLORS[resolved]);
}

export function setTheme(theme) {
  const next = THEMES.includes(theme) ? theme : 'system';
  try {
    if (next === 'system') localStorage.removeItem(STORAGE_KEY);
    else localStorage.setItem(STORAGE_KEY, next);
  } catch {
    // storage unavailable (private mode): the choice still applies to this page
  }
  applyTheme(next);
  listeners.forEach((fn) => fn(next));
}

// Call once at startup: follows the OS while the choice is 'system'.
export function initTheme() {
  applyTheme(getTheme());
  if (typeof window === 'undefined' || !window.matchMedia) return;
  const query = window.matchMedia('(prefers-color-scheme: dark)');
  const onChange = () => { if (getTheme() === 'system') applyTheme('system'); };
  if (query.addEventListener) query.addEventListener('change', onChange);
  else if (query.addListener) query.addListener(onChange);
}

// [theme, setTheme] — every component using the hook sees the same value.
export function useTheme() {
  const [theme, setLocal] = useState(getTheme);
  useEffect(() => {
    listeners.add(setLocal);
    return () => { listeners.delete(setLocal); };
  }, []);
  const change = useCallback((next) => setTheme(next), []);
  return [theme, change];
}
